import { HilModuleResolutionType, mapToHilSignal } from '@cadstrom/backend/shared';
import { SignalDirection, Utils } from '@cadstrom/sigma-engine-shared';

import type {
	HilModuleResolution,
	HilPackage,
	HilSigmaModelResolutionProperties,
	HilSignal,
} from '@cadstrom/backend/shared';
import type { Component, Module, ProjectModule, SignalAttributes } from '@cadstrom/sigma-engine-shared';

/**
 * The subset of the project core model needed to build a module pinout. Structurally satisfied by
 * `ModelWithNetStats`/`ProjectModel`.
 */
export interface PinoutCoreModel {
	components: { [componentId: string]: Component };
	designs: { [designId: string]: { modelType: 'pcb' | 'schematic'; modules: Module[] } };
}

export interface ModulePinoutRow {
	/** Unique pin reference within the component; suitable as a React key. */
	pinRef: string;
	/** Pin number as printed on the symbol; falls back to the raw pin ref. */
	pinNumber: string;
	/** The signal associated with this pin. Null when the pin has no signal. */
	signal: HilSignal | null;
	/** Human-readable summary of the signal attributes (e.g. "Power", "Analog"), or null if none apply. */
	type: string | null;
	/** Human-readable signal direction (e.g. "In", "Out", "I/O"), or null when unknown. */
	direction: string | null;
	/** Name of the net the pin is connected to in the design, or null when unconnected/unknown. */
	net: string | null;
}

const directionLabels: Record<SignalDirection, string> = {
	[SignalDirection.Input]: 'In',
	[SignalDirection.Output]: 'Out',
	[SignalDirection.BiDirectionalIO]: 'I/O',
};

/**
 * Derives a label summarizing a signal's attributes, picking the most specific category that applies.
 *
 * @param attributes - The signal's attributes, if any
 * @returns A short label such as "Ground" or "Analog", or null when no attribute is set
 */
function getSignalTypeLabel(attributes: SignalAttributes | undefined): string | null {
	if (!attributes) {
		return null;
	}
	if (attributes.noConnect) {
		return 'NC';
	}
	if (attributes.ground) {
		return 'Ground';
	}
	if (attributes.power) {
		return 'Power';
	}
	if (attributes.analog) {
		return 'Analog';
	}
	if (attributes.digital) {
		return 'Digital';
	}
	return null;
}

function buildPinNets(coreModel: PinoutCoreModel, projectModule: ProjectModule): Record<string, string> {
	const pinNets: Record<string, string> = {};
	for (const instance of projectModule.instances) {
		const design = coreModel.designs[instance.designId];
		if (design?.modelType !== 'schematic') {
			continue;
		}
		const designModule = design.modules.find((m) => m.ref === instance.ref);
		if (!designModule) {
			continue;
		}
		for (const [pinRef, net] of Object.entries(designModule.pinNets)) {
			pinNets[pinRef] ??= net;
		}
	}
	return pinNets;
}

export function deriveModulePinoutRows({
	coreModel,
	projectModule,
	moduleResolution,
	pkg,
}: {
	coreModel: PinoutCoreModel | undefined;
	projectModule: ProjectModule | undefined;
	moduleResolution: HilModuleResolution | null;
	pkg: HilPackage | null;
}): ModulePinoutRow[] {
	const isPackageStoreResolution = moduleResolution?.resolutionType === HilModuleResolutionType.PackageStore;

	if (isPackageStoreResolution && pkg) {
		const pinNumberToNet = coreModel && projectModule ? buildPinNumberToNet(coreModel, projectModule) : undefined;
		return getModulePinoutRowsFromPackageStoreResolution(pkg, pinNumberToNet);
	}

	if (moduleResolution !== null && !isPackageStoreResolution) {
		const signalIdToNet = coreModel && projectModule ? buildSignalIdToNet(coreModel, projectModule) : undefined;
		return getModulePinoutRowsFromSigmaModelResolution(moduleResolution.sigmaModelResolutionProperties, signalIdToNet);
	}

	return coreModel && projectModule ? getModulePinoutRowsWithoutResolution(coreModel, projectModule) : [];
}

export function buildPinNumberToNet(coreModel: PinoutCoreModel, projectModule: ProjectModule): Record<string, string> {
	const pinNets = buildPinNets(coreModel, projectModule);
	const component = coreModel.components[projectModule.componentRef];
	if (!component) {
		return {};
	}
	const pinNumberToNet: Record<string, string> = {};
	for (const symbol of component.symbols) {
		for (const pin of symbol.pins) {
			const net = pinNets[pin.ref];
			if (net !== undefined) {
				pinNumberToNet[pin.number] ??= net;
			}
		}
	}
	return pinNumberToNet;
}

export function buildSignalIdToNet(coreModel: PinoutCoreModel, projectModule: ProjectModule): Record<string, string> {
	const pinNets = buildPinNets(coreModel, projectModule);
	const component = coreModel.components[projectModule.componentRef];
	if (!component) {
		return {};
	}
	const signalIdToNet: Record<string, string> = {};
	for (const signal of component.signals) {
		for (const pinRef of signal.pinRefs) {
			if (pinRef === null) {
				continue;
			}
			const net = pinNets[pinRef];
			if (net !== undefined) {
				signalIdToNet[String(signal.id)] ??= net;
			}
		}
	}
	return signalIdToNet;
}

export function getModulePinoutRowsFromPackageStoreResolution(
	pkg: HilPackage,
	pinNumberToNet?: Record<string, string>,
): ModulePinoutRow[] {
	const rows: ModulePinoutRow[] = [];
	for (const signal of pkg.signals) {
		if (signal.isCadstromInternal) {
			continue;
		}
		const pinNumbers = pkg.pinMap[String(signal.id)] ?? [];
		if (pinNumbers.length > 0) {
			for (const pinNumber of pinNumbers) {
				rows.push({
					pinRef: `${signal.id}-${pinNumber}`,
					pinNumber,
					signal,
					type: getSignalTypeLabel(signal.attributes),
					direction: signal.direction !== null ? directionLabels[signal.direction] : null,
					net: pinNumberToNet?.[pinNumber] ?? null,
				});
			}
		} else {
			rows.push({
				pinRef: String(signal.id),
				pinNumber: '',
				signal,
				type: getSignalTypeLabel(signal.attributes),
				direction: signal.direction !== null ? directionLabels[signal.direction] : null,
				net: null,
			});
		}
	}
	return rows.toSorted((a, b) => Utils.naturalStringSort(a.pinNumber, b.pinNumber));
}

export function getModulePinoutRowsFromSigmaModelResolution(
	properties: HilSigmaModelResolutionProperties,
	signalIdToNet?: Record<string, string>,
): ModulePinoutRow[] {
	return properties.signals
		.filter((s) => !s.isCadstromInternal)
		.map((signal) => ({
			pinRef: String(signal.id),
			pinNumber: '',
			signal,
			type: getSignalTypeLabel(signal.attributes),
			direction: signal.direction !== null ? directionLabels[signal.direction] : null,
			net: signalIdToNet?.[String(signal.id)] ?? null,
		}))
		.toSorted((a, b) => a.signal.name.localeCompare(b.signal.name));
}

export function getModulePinoutRowsWithoutResolution(
	coreModel: PinoutCoreModel,
	projectModule: ProjectModule,
): ModulePinoutRow[] {
	const component = coreModel.components[projectModule.componentRef];
	if (!component) {
		return [];
	}

	const pinNets = buildPinNets(coreModel, projectModule);
	const pinsByRef = new Map(component.symbols.flatMap((symbol) => symbol.pins).map((pin) => [pin.ref, pin]));

	const rows: ModulePinoutRow[] = [];
	for (const signal of component.signals) {
		if (signal.isCadstromInternal) {
			continue;
		}

		for (const pinRef of signal.pinRefs) {
			if (pinRef === null) {
				continue;
			}

			const pin = pinsByRef.get(pinRef);
			rows.push({
				pinRef,
				pinNumber: pin?.number ?? pinRef,
				signal: mapToHilSignal(signal),
				type: getSignalTypeLabel(signal.attributes),
				direction: signal.signalDirection !== undefined ? directionLabels[signal.signalDirection] : null,
				net: pinNets[pinRef] ?? null,
			});
		}
	}

	return rows.toSorted((a, b) => Utils.naturalStringSort(a.pinNumber, b.pinNumber));
}
